import React from 'react';
import { motion } from "framer-motion";

const f = (d = 0) => ({
  initial: { opacity: 0, y: 28 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, delay: d, ease: [0.16, 1, 0.3, 1] },
});

const Redeem = () => (
  <>
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;1,400;1,700&family=DM+Sans:wght@300;400;500&family=DM+Mono:wght@300;400&display=swap');

      .rd-root {
        background: #fff;
        font-family: Arial, Helvetica, sans-serif;
        color: #111;
        -webkit-font-smoothing: antialiased;
        min-height: 100vh;
        padding-top: 150px;
      }

      /* ══════════════════════════
         HERO — full-width image with
         large title overlaid at bottom
      ══════════════════════════ */
      .rd-hero {
        position: relative;
        width: 90%;
        height: min(55vh, 520px);
        overflow: hidden;
        background: #0c1520;
        margin: 0 auto;
        border-radius: 20px;
      }
      .rd-hero img {
        width: 100%; height: 100%;
        object-fit: cover; object-position: center 25%;
        filter: brightness(0.48) saturate(0.7);
        display: block;
        transform: scale(1.04);
        transition: transform 9s ease;
        border-radius: 20px;
      }
      .rd-hero:hover img { transform: scale(1); }

      /* dual-layer vignette */
      .rd-hero-vignette {
        position: absolute; inset: 0;
        background:
          linear-gradient(to top, rgba(0, 0, 0, 0.03) 0%, rgba(0, 0, 0, 0.63) 55%, transparent 100%),
          linear-gradient(to right, rgba(0, 0, 0, 0) 0%, transparent 50%);
        pointer-events: none;
      }

      .rd-hero-content {
        position: absolute; inset: 0;
        display: flex; flex-direction: column;
        justify-content: flex-end;
        padding: clamp(2rem, 5vw, 4rem) clamp(2rem, 7vw, 5rem);
        z-index: 2;
      }
      .rd-hero-eyebrow {
        font-family: Arial, Helvetica, sans-serif;
        font-size: 8.5px; letter-spacing: 0.3em; text-transform: uppercase;
        color: rgba(255,255,255,0.45);
        display: flex; align-items: center; gap: 10px;
        margin-bottom: 1rem;
      }
      .rd-hero-eyebrow::before { content:''; width:26px; height:1px; background:rgba(255,255,255,0.3); }

      .rd-hero-title {
        font-family: Arial, Helvetica, sans-serif;
        font-size: clamp(2.4rem, 7vw, 6rem);
        font-weight: 700;
        line-height: 0.9; letter-spacing: -0.035em;
        color: #fff;
      }
      .rd-hero-title em {
        display: block;
        font-style: italic; font-weight: 400;
        color: rgba(255,255,255,0.55);
      }

      /* ── HERO mobile ── */
      @media (max-width: 600px) {
        .rd-root {
          padding-top: 0;
        }
        .rd-hero {
          width: 94%;
          height: 200px;
          min-height: unset;
          border-radius: 16px;
          margin: 120px auto 0;
        }
        .rd-hero img {
          border-radius: 16px;
          filter: brightness(0.50) saturate(0.85);
        }
        .rd-hero-content {
          padding: 0.85rem 1rem 1rem;
        }
        .rd-hero-eyebrow {
          font-size: 7.5px;
          letter-spacing: 0.16em;
          margin-bottom: 0.35rem;
          gap: 6px;
        }
        .rd-hero-eyebrow::before {
          width: 14px;
          height: 1px;
        }
        .rd-hero-title {
          font-size: clamp(1.5rem, 5vw, 2.5rem);
          line-height: 1.1;
        }
      }

      /* top-right badge */
      .rd-hero-badge {
        position: absolute; top: 1.5rem; right: 1.8rem; z-index: 3;
        font-family: Arial, Helvetica, sans-serif;
        font-size: 7.5px; letter-spacing: 0.22em; text-transform: uppercase;
        color: rgba(255,255,255,0.5);
        border: 1px solid rgba(255,255,255,0.14);
        backdrop-filter: blur(8px);
        background: rgba(0,0,0,0.2);
        padding: 0.4rem 0.9rem; border-radius: 2px;
      }

      /* ══════════════════════════
         SPONSOR BANNER — full width
         image strip with soft border
      ══════════════════════════ */
      .rd-banner {
        border-top: 1px solid #eee;
        border-bottom: 1px solid #eee;
        overflow: hidden;
        background: #fafafa;
      }
      .rd-banner img {
        width: 100%;
        max-height: 220px;
        object-fit: cover;
        object-position: center;
        display: block;
        filter: saturate(0.9);
      }

      /* ══════════════════════════
         INTRO ROW — big stat + intro text
      ══════════════════════════ */
      .rd-intro {
        max-width: 1100px; margin: 0 auto;
        padding: clamp(3rem, 6vw, 5rem) clamp(2rem, 5vw, 4.5rem) 0;
        display: grid;
        grid-template-columns: 240px 1fr;
        gap: clamp(3rem, 6vw, 5rem);
        align-items: start;
      }
      @media (max-width: 700px) {
        .rd-intro { 
          grid-template-columns: 1fr !important; 
          gap: 1.5rem !important; 
        }
        .rd-intro-stat { 
          display: flex !important; 
          justify-content: space-around !important; 
          align-items: center !important; 
          gap: 2rem !important; 
          border-right: none !important; 
          border-bottom: 1px solid #eee !important; 
          padding-bottom: 1.5rem !important; 
          padding-right: 0 !important; 
          flex-direction: row !important;
        }
        .rd-stat-block {
          text-align: center !important;
          flex: 1 !important;
        }
        .rd-stat-divider { display: none !important; }
      }

      .rd-intro-stat {
        border-right: 1px solid #eee;
        padding-right: clamp(2rem, 4vw, 3.5rem);
        display: flex; flex-direction: column; gap: 1.4rem;
      }
      .rd-stat-block {}
      .rd-stat-val {
        font-family: Arial, Helvetica, sans-serif;
        font-size: 3rem; font-weight: 700;
        color: #1d4ed8; line-height: 1;
      }
      .rd-stat-label {
        font-family: Arial, Helvetica, sans-serif;
        font-size: 7.5px; letter-spacing: 0.2em; text-transform: uppercase;
        color: #bbb; margin-top: 3px;
      }
      .rd-stat-divider { height: 1px; background: #f0f0f0; }

      .rd-intro-text {}
      .rd-intro-label {
        font-family: Arial, Helvetica, sans-serif;
        font-size: 8px; letter-spacing: 0.28em; text-transform: uppercase;
        color: #1d4ed8; display: flex; align-items: center; gap: 8px;
        margin-bottom: 1.2rem;
      }
      .rd-intro-label::before { content:''; width:14px; height:1px; background:#1d4ed8; }
      .rd-intro-heading {
        font-family: Arial, Helvetica, sans-serif;
        font-size: clamp(1.8rem, 3.5vw, 2.8rem);
        font-weight: 400; line-height: 1.08;
        letter-spacing: -0.025em; color: #0a0a0a;
        margin-bottom: 1.4rem;
      }
      .rd-intro-heading em { font-style: italic; }
      .rd-intro-p {
        font-size: 1.02rem; line-height: 1.88;
        color: #444; font-weight: 300;
      }
      .rd-intro-p::first-letter {
        font-family: Arial, Helvetica, sans-serif;
        font-size: 4em; font-weight: 700;
        float: left; line-height: 0.75;
        margin: 0.06em 0.1em 0 0; color: #0a0a0a;
      }

      /* ══════════════════════════
         STORY CARDS — horizontal
         scroll on mobile, 3-col on desktop
      ══════════════════════════ */
      .rd-stories {
        max-width: 1100px; margin: 0 auto;
        padding: clamp(1.2rem, 2.5vw, 2rem) clamp(2rem, 5vw, 4.5rem) clamp(3rem, 6vw, 5rem);
      }
      .rd-stories-label {
        font-family: Arial, Helvetica, sans-serif;
        font-size: 8px; letter-spacing: 0.28em; text-transform: uppercase;
        color: #1d4ed8; display: flex; align-items: center; gap: 8px;
        margin-bottom: 1.5rem;
      }
      .rd-stories-label::before { content:''; width:14px; height:1px; background:#1d4ed8; }
      .rd-stories-label::after  { content:''; flex:1; height:1px; background:#f0f0f0; }

      .rd-cards {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 1.5rem;
      }
      @media (max-width: 740px) { .rd-cards { grid-template-columns: 1fr; } }

      .rd-card {
        border: 1px solid #eee;
        border-radius: 10px;
        padding: 1.8rem 1.6rem;
        position: relative;
        transition: border-color 0.2s, box-shadow 0.2s;
      }
      .rd-card:hover {
        border-color: #dbeafe;
        box-shadow: 0 4px 24px rgba(29,78,216,0.06);
      }
      .rd-card-icon {
        width: 36px; height: 36px; border-radius: 8px;
        background: #eff6ff; border: 1px solid #dbeafe;
        display: flex; align-items: center; justify-content: center;
        margin-bottom: 1rem;
        font-size: 1rem;
      }
      .rd-card-title {
        font-family: Arial, Helvetica, sans-serif;
        font-size: 1.1rem; font-weight: 700;
        color: #0a0a0a; margin-bottom: 0.7rem;
        line-height: 1.2;
      }
      .rd-card-p {
        font-size: 0.875rem; line-height: 1.8;
        color: #555; font-weight: 300;
      }
      .rd-card-num {
        position: absolute; top: 1.2rem; right: 1.4rem;
        font-family: Arial, Helvetica, sans-serif;
        font-size: 2.4rem; font-weight: 700;
        color: rgba(29,78,216,0.06);
        line-height: 1; letter-spacing: -0.05em;
      }

      /* ══════════════════════════
         QUOTE + SPONSOR NOTE
      ══════════════════════════ */
      .rd-bottom {
        max-width: 1100px; margin: 0 auto;
        padding: 0 clamp(2rem, 5vw, 4.5rem) clamp(4rem, 8vw, 7rem);
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: clamp(2rem, 5vw, 4rem);
        align-items: start;
      }
      @media (max-width: 700px) { .rd-bottom { grid-template-columns: 1fr; } }

      .rd-quote {
        display: grid; grid-template-columns: 4px 1fr; gap: 1.5rem;
        padding: 1.5rem 0;
        border-top: 1px solid #eee; border-bottom: 1px solid #eee;
      }
      .rd-quote-bar { background: #1d4ed8; border-radius: 2px; }
      .rd-quote-text {
        font-family: Arial, Helvetica, sans-serif;
        font-size: clamp(1rem, 2vw, 1.35rem);
        font-style: italic; font-weight: 400;
        line-height: 1.55; color: #1a1a2e;
      }

      .rd-sponsor {
        padding: 1.8rem;
        background: #f5f8ff;
        border: 1px solid #dbeafe;
        border-radius: 10px;
      }
      .rd-sponsor-label {
        font-family: Arial, Helvetica, sans-serif;
        font-size: 7.5px; letter-spacing: 0.2em; text-transform: uppercase;
        color: #93aee0; margin-bottom: 0.6rem;
      }
      .rd-sponsor-name {
        font-family: Arial, Helvetica, sans-serif;
        font-size: 1.15rem; font-weight: 700;
        color: #1d4ed8; margin-bottom: 0.6rem;
        line-height: 1.2;
      }
      .rd-sponsor-desc {
        font-size: 0.875rem; line-height: 1.75;
        color: #444; font-weight: 300;
      }
      .rd-sponsor-tags {
        display: flex; flex-wrap: wrap; gap: 0.4rem; margin-top: 1rem;
      }
      .rd-tag {
        font-family: Arial, Helvetica, sans-serif;
        font-size: 7px; letter-spacing: 0.16em; text-transform: uppercase;
        border: 1px solid #dbeafe; background: #fff;
        color: #1d4ed8; padding: 3px 10px; border-radius: 100px;
      }

      /* footer */
      .rd-foot {
        border-top: 1px solid #eee;
        max-width: 1100px; margin: 0 auto;
        padding: 1.5rem clamp(2rem, 5vw, 4.5rem) 2.5rem;
        display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 0.6rem;
      }
      .rd-foot-l { font-family: Arial, Helvetica, sans-serif; font-size: 8px; letter-spacing: 0.18em; text-transform: uppercase; color: #ccc; }
      .rd-foot-r { font-family: Arial, Helvetica, sans-serif; font-size: 0.8rem; font-style: italic; color: #ccc; }
    `}</style>

    <div className="rd-root">

      {/* ── HERO ── */}
      <motion.div className="rd-hero" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }}>
        <img
          src="https://firebasestorage.googleapis.com/v0/b/bcs-public-portal.appspot.com/o/BCS_WEBITE_ASSETS%2Frehome.jpg?alt=media&token=69d6bba7-7e93-4732-8ca0-a818c7cfcaab"
          alt="Mother Elizabeth Obu Redeemed Home"
        />
        <div className="rd-hero-vignette" />
        <div className="rd-hero-badge">Est. 1967</div>
        {/* <div className="rd-hero-content">
          <motion.p className="rd-hero-eyebrow" {...f(0.3)}>Brotherhood of the Cross and Star</motion.p>
          <motion.h1 className="rd-hero-title" {...f(0.45)}>
            Redeemed<br /><em>Home of Love</em>
          </motion.h1>
        </div> */}
      </motion.div>



      {/* ── INTRO ROW ── */}
      <motion.div className="rd-intro" {...f(0.55)}>
        <div className="rd-intro-stat">
          <div className="rd-stat-block">
            <div className="rd-stat-val">1967</div>
            <div className="rd-stat-label">Founded during civil war</div>
          </div>
          <div className="rd-stat-divider" />
          <div className="rd-stat-block">
            <div className="rd-stat-val">CAC</div>
            <div className="rd-stat-label">Registered with Federal Govt.</div>
          </div>
          <div className="rd-stat-divider" />
          <div className="rd-stat-block">
            <div className="rd-stat-val">7</div>
            <div className="rd-stat-label">Registered trustees</div>
          </div>
        </div>

        <div className="rd-intro-text">
          <p className="rd-intro-label">Origins</p>
          <h2 className="rd-intro-heading">Founded in <em>mercy</em><br />during wartime</h2>
          <p className="rd-intro-p">
            The Home was established by the Holy Mother, Esu-Elizabeth Olumba Obu,
            during the Nigerian civil war of 1967–1970. At that time there were many
            children abandoned by parents who had died or fled. The Holy Mother took
            them all in — feeding, protecting, and giving them new life.
          </p>
        </div>
      </motion.div>

      {/* ── STORY CARDS ── */}
      <div className="rd-stories">
        <p className="rd-stories-label">The Full Story</p>
        <motion.div className="rd-cards" {...f(0.65)}>
          {[
            {
              icon: "🕊️",
              title: "A Wartime Act of Mercy",
              text: "During the 1967–1970 civil war, the Holy Mother sheltered abandoned children whose parents had perished or fled. She fed them, protected them from the war, and gave them new life within the Holy Family.",
            },
            {
              icon: "🎓",
              title: "Education & Restoration",
              text: "After the war, the children lived with the Holy Family, were sent to school, and treated to the good life as members of the family — many even attending higher institutions abroad, including the United States of America.",
            },
            {
              icon: "🏠",
              title: "A Living Legacy Today",
              text: "That humble beginning is what we have today: the Mother Elizabeth Obu Redeemed Home of Love — registered with the CAC and Cross River State Humanity & Social Welfare. Today the Holy Father is the sole sponsor.",
            },
          ].map((c, i) => (
            <div className="rd-card" key={i}>
              <div className="rd-card-num">0{i + 1}</div>
              <div className="rd-card-icon">{c.icon}</div>
              <h3 className="rd-card-title">{c.title}</h3>
              <p className="rd-card-p">{c.text}</p>
            </div>
          ))}
        </motion.div>
      </div>

      {/* ── QUOTE + SPONSOR NOTE ── */}
      <motion.div className="rd-bottom" {...f(0.75)}>
        <div className="rd-quote">
          <div className="rd-quote-bar" />
          <p className="rd-quote-text">
            "Today, many vulnerable children — from BCS and outside the Fold —
            are taken care of at the Home, a living testament to the Holy Mother's
            legacy of love and compassion."
          </p>
        </div>
        <div className="rd-sponsor">
          <p className="rd-sponsor-label">Sole Sponsor</p>
          <p className="rd-sponsor-name">His Holiness Olumba Olumba Obu</p>
          <p className="rd-sponsor-desc">
            The Holy Father continues the legacy of the Holy Mother as the sole
            sponsor of the Redeemed Home, ensuring every child receives care,
            education, and love.
          </p>
          <div className="rd-sponsor-tags">
            <span className="rd-tag">CAC Registered</span>
            <span className="rd-tag">Cross River State</span>
            <span className="rd-tag">Federal Govt. Nigeria</span>
          </div>
        </div>
      </motion.div>

      {/* ── FOOTER ── */}
      <div className="rd-foot">
        <span className="rd-foot-l">Mother Elizabeth Obu Redeemed Home · BCS</span>
        <span className="rd-foot-r">Calabar, Nigeria · Est. 1967</span>
      </div>

    </div>
  </>
);

export default Redeem;
